import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Code2, Search, Filter, Sparkles, Download, Upload, RefreshCw, 
  Loader2, AlertTriangle, CheckCircle2, XCircle, Clock, TrendingUp,
  FileCode, Zap, Brain, ChevronDown, ChevronRight, Eye, Edit3, Save,
  X, Plus, Trash2, BarChart3, Target
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CustomCodeAssistant = ({ project }) => {
  const [codeObjects, setCodeObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBreakType, setFilterBreakType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedObject, setSelectedObject] = useState(null);
  const [analyzing, setAnalyzing] = useState(null);
  const [bulkAnalyzing, setBulkAnalyzing] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [sortBy, setSortBy] = useState('priority'); // priority, complexity, usage, effort

  useEffect(() => {
    fetchCodeObjects();
  }, [project.id]);

  const fetchCodeObjects = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/custom-code`);
      setCodeObjects(response.data);
    } catch (error) {
      console.error('Error fetching code objects:', error);
    } finally {
      setLoading(false);
    }
  };

  const analyzeCode = async (codeObject) => {
    setAnalyzing(codeObject.id);
    try {
      const response = await axios.post(`${API}/ai/analyze-code`, {
        project_id: project.id,
        analysis_type: 'code_review',
        context: codeObject
      });
      
      // Update the code object with AI suggestion
      const updatedObjects = codeObjects.map(obj => 
        obj.id === codeObject.id 
          ? { ...obj, ai_suggestion: response.data.result.ai_suggestion }
          : obj
      );
      setCodeObjects(updatedObjects);
      
      // Show the analysis in selected object view
      setSelectedObject({ ...codeObject, ai_suggestion: response.data.result.ai_suggestion });
    } catch (error) {
      console.error('Error analyzing code:', error);
      alert('Failed to analyze code. Please try again.');
    } finally {
      setAnalyzing(null);
    }
  };

  const bulkAnalyze = async () => {
    setBulkAnalyzing(true);
    const highPriorityObjects = codeObjects.filter(obj => 
      ['high', 'critical'].includes(obj.priority) && !obj.ai_suggestion
    );
    
    try {
      for (const obj of highPriorityObjects.slice(0, 5)) {
        await analyzeCode(obj);
      }
      alert(`Bulk analysis complete! Analyzed ${Math.min(highPriorityObjects.length, 5)} high-priority objects.`);
    } catch (error) {
      console.error('Bulk analysis error:', error);
    } finally {
      setBulkAnalyzing(false);
    }
  };

  const exportReport = () => {
    const report = filteredAndSortedObjects.map(obj => ({
      name: obj.object_name,
      type: obj.object_type,
      priority: obj.priority,
      lines: obj.lines_of_code,
      complexity: obj.complexity_score,
      effort_hours: obj.remediation_effort_hours,
      status: obj.remediation_status,
      break_type: obj.break_type || 'None'
    }));
    
    const csv = [
      'Object Name,Type,Priority,Lines of Code,Complexity,Effort (hours),Status,Break Type',
      ...report.map(r => `${r.name},${r.type},${r.priority},${r.lines},${r.complexity},${r.effort_hours},${r.status},${r.break_type}`)
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project.name}_code_analysis.csv`;
    a.click();
  };

  // Filtering and sorting logic
  const filteredAndSortedObjects = codeObjects
    .filter((obj) => {
      if (filterPriority !== 'all' && obj.priority !== filterPriority) return false;
      if (filterStatus !== 'all' && obj.remediation_status !== filterStatus) return false;
      if (filterBreakType !== 'all' && obj.break_type !== filterBreakType) return false;
      if (searchTerm && !obj.object_name.toLowerCase().includes(searchTerm.toLowerCase()) && 
          !obj.description?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'priority':
          const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        case 'complexity':
          return b.complexity_score - a.complexity_score;
        case 'usage':
          return b.usage_frequency - a.usage_frequency;
        case 'effort':
          return b.remediation_effort_hours - a.remediation_effort_hours;
        default:
          return 0;
      }
    });

  const priorityColors = {
    low: 'bg-green-100 text-green-800 border-green-200',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    high: 'bg-orange-100 text-orange-800 border-orange-200',
    critical: 'bg-red-100 text-red-800 border-red-200'
  };

  const statusColors = {
    pending: 'bg-gray-100 text-gray-800',
    in_progress: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    not_required: 'bg-purple-100 text-purple-800'
  };

  // Statistics
  const stats = {
    total: codeObjects.length,
    critical: codeObjects.filter(o => o.priority === 'critical').length,
    high: codeObjects.filter(o => o.priority === 'high').length,
    pending: codeObjects.filter(o => o.remediation_status === 'pending').length,
    completed: codeObjects.filter(o => o.remediation_status === 'completed').length,
    total_effort: codeObjects.reduce((sum, o) => sum + o.remediation_effort_hours, 0),
    avg_complexity: (codeObjects.reduce((sum, o) => sum + o.complexity_score, 0) / codeObjects.length).toFixed(1)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Code2 className="w-12 h-12" />
            <div>
              <h2 className="text-3xl font-bold">ABAP Code Assistant</h2>
              <p className="text-cyan-100 mt-1">AI-powered custom code analysis for S/4HANA compatibility</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={bulkAnalyze}
              disabled={bulkAnalyzing}
              className="px-6 py-3 bg-white text-cyan-700 rounded-xl hover:bg-cyan-50 transition-colors font-medium flex items-center space-x-2 disabled:opacity-50"
            >
              {bulkAnalyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  <span>Bulk AI Analyze</span>
                </>
              )}
            </button>
            <button
              onClick={() => setShowUpload(true)}
              className="px-6 py-3 bg-white/20 hover:bg-white/30 rounded-xl transition-colors font-medium flex items-center space-x-2"
            >
              <Upload className="w-5 h-5" />
              <span>Upload Code</span>
            </button>
            <button
              onClick={exportReport}
              className="px-6 py-3 bg-white/20 hover:bg-white/30 rounded-xl transition-colors font-medium flex items-center space-x-2"
            >
              <Download className="w-5 h-5" />
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Dashboard */}
      <div className="grid md:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
          <p className="text-xs text-gray-600 mb-1 font-medium">Total Objects</p>
          <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-red-500">
          <p className="text-xs text-gray-600 mb-1 font-medium">Critical</p>
          <p className="text-3xl font-bold text-red-600">{stats.critical}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-orange-500">
          <p className="text-xs text-gray-600 mb-1 font-medium">High Priority</p>
          <p className="text-3xl font-bold text-orange-600">{stats.high}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-yellow-500">
          <p className="text-xs text-gray-600 mb-1 font-medium">Pending</p>
          <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-green-500">
          <p className="text-xs text-gray-600 mb-1 font-medium">Completed</p>
          <p className="text-3xl font-bold text-green-600">{stats.completed}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-purple-500">
          <p className="text-xs text-gray-600 mb-1 font-medium">Total Effort</p>
          <p className="text-2xl font-bold text-purple-600">{Math.round(stats.total_effort)}h</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search code objects..."
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Priority Filter */}
          <div>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Priorities</option>
              <option value="critical">🔴 Critical</option>
              <option value="high">🟠 High</option>
              <option value="medium">🟡 Medium</option>
              <option value="low">🟢 Low</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="pending">⏳ Pending</option>
              <option value="in_progress">🔄 In Progress</option>
              <option value="completed">✅ Completed</option>
              <option value="not_required">➖ Not Required</option>
            </select>
          </div>

          {/* Sort By */}
          <div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="priority">Sort by Priority</option>
              <option value="complexity">Sort by Complexity</option>
              <option value="usage">Sort by Usage</option>
              <option value="effort">Sort by Effort</option>
            </select>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Filter className="w-4 h-4" />
            <span>Showing <strong className="text-gray-900">{filteredAndSortedObjects.length}</strong> of <strong className="text-gray-900">{stats.total}</strong> objects</span>
          </div>
          <button
            onClick={fetchCodeObjects}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center space-x-2 text-sm font-medium text-gray-700"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Code Objects Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Panel - Object List */}
        <div className="lg:col-span-2 space-y-4">
          {filteredAndSortedObjects.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
              <FileCode className="w-20 h-20 mx-auto mb-6 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No code objects found</h3>
              <p className="text-gray-600">Try adjusting your filters or upload code objects</p>
            </div>
          ) : (
            filteredAndSortedObjects.map((obj) => (
              <CodeObjectCard
                key={obj.id}
                codeObject={obj}
                onSelect={setSelectedObject}
                onAnalyze={analyzeCode}
                analyzing={analyzing === obj.id}
                isSelected={selectedObject?.id === obj.id}
                priorityColors={priorityColors}
                statusColors={statusColors}
              />
            ))
          )}
        </div>

        {/* Right Panel - Details */}
        <div className="lg:col-span-1">
          {selectedObject ? (
            <CodeObjectDetails 
              codeObject={selectedObject} 
              onClose={() => setSelectedObject(null)}
              onAnalyze={analyzeCode}
              analyzing={analyzing === selectedObject.id}
            />
          ) : (
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 text-center sticky top-8">
              <Eye className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Object Selected</h3>
              <p className="text-sm text-gray-600">Click on a code object to view detailed analysis</p>
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUpload && <UploadCodeModal onClose={() => setShowUpload(false)} projectId={project.id} onUploadComplete={fetchCodeObjects} />}
    </div>
  );
};

// Code Object Card Component
const CodeObjectCard = ({ codeObject, onSelect, onAnalyze, analyzing, isSelected, priorityColors, statusColors }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      onClick={() => onSelect(codeObject)}
      className={`bg-white border-2 rounded-2xl p-5 transition-all duration-300 cursor-pointer ${
        isSelected 
          ? 'border-blue-500 shadow-xl ring-4 ring-blue-100' 
          : 'border-gray-200 hover:border-blue-300 hover:shadow-lg'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <FileCode className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-bold text-gray-900">{codeObject.object_name}</h3>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-lg text-xs font-semibold border-2 ${priorityColors[codeObject.priority]}`}>
              {codeObject.priority.toUpperCase()}
            </span>
            <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium">
              {codeObject.object_type}
            </span>
            <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${statusColors[codeObject.remediation_status]}`}>
              {codeObject.remediation_status.replace('_', ' ')}
            </span>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          {expanded ? <ChevronDown className="w-5 h-5 text-gray-600" /> : <ChevronRight className="w-5 h-5 text-gray-600" />}
        </button>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 mb-4">{codeObject.description}</p>

      {/* Metrics Grid */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-xs text-blue-600 font-medium mb-1">Lines</p>
          <p className="text-lg font-bold text-blue-900">{codeObject.lines_of_code.toLocaleString()}</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-3">
          <p className="text-xs text-purple-600 font-medium mb-1">Complexity</p>
          <p className="text-lg font-bold text-purple-900">{codeObject.complexity_score}/10</p>
        </div>
        <div className="bg-green-50 rounded-lg p-3">
          <p className="text-xs text-green-600 font-medium mb-1">Usage</p>
          <p className="text-lg font-bold text-green-900">{codeObject.usage_frequency.toLocaleString()}</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-3">
          <p className="text-xs text-orange-600 font-medium mb-1">Effort</p>
          <p className="text-lg font-bold text-orange-900">{codeObject.remediation_effort_hours}h</p>
        </div>
      </div>

      {/* Break Type Warning */}
      {codeObject.break_type && (
        <div className="mb-4 p-3 bg-red-50 border-2 border-red-200 rounded-xl flex items-center space-x-2">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-bold text-red-900">Breaking Change Detected</p>
            <p className="text-xs text-red-700 capitalize">{codeObject.break_type.replace('_', ' ')}</p>
          </div>
        </div>
      )}

      {/* AI Analysis Status */}
      {codeObject.ai_suggestion ? (
        <div className="mb-4 p-3 bg-green-50 border-2 border-green-200 rounded-xl flex items-center space-x-2">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          <span className="text-sm font-semibold text-green-700">AI Analysis Complete</span>
        </div>
      ) : (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onAnalyze(codeObject);
          }}
          disabled={analyzing}
          className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all font-medium disabled:opacity-50 flex items-center justify-center space-x-2"
        >
          {analyzing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Analyzing...</span>
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              <span>AI Analyze</span>
            </>
          )}
        </button>
      )}

      {/* Expanded Details */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-600">Last Modified:</span>
              <p className="font-medium text-gray-900">
                {codeObject.last_modified ? new Date(codeObject.last_modified).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <div>
              <span className="text-gray-600">Created:</span>
              <p className="font-medium text-gray-900">
                {new Date(codeObject.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Code Object Details Panel
const CodeObjectDetails = ({ codeObject, onClose, onAnalyze, analyzing }) => {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 sticky top-8 max-h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">{codeObject.object_name}</h3>
            <p className="text-sm text-gray-600">{codeObject.object_type}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex space-x-2 border-b border-gray-200">
          {['overview', 'analysis', 'remediation'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium text-sm transition-colors capitalize ${
                activeTab === tab
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'overview' && (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Description</p>
              <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">{codeObject.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-xs text-blue-600 font-medium mb-1">Lines of Code</p>
                <p className="text-2xl font-bold text-blue-900">{codeObject.lines_of_code.toLocaleString()}</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-xs text-purple-600 font-medium mb-1">Complexity</p>
                <p className="text-2xl font-bold text-purple-900">{codeObject.complexity_score}/10</p>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Usage Statistics</p>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-xs text-green-600 font-medium mb-1">Frequency</p>
                <p className="text-2xl font-bold text-green-900">{codeObject.usage_frequency.toLocaleString()}</p>
                <p className="text-xs text-green-700 mt-1">executions</p>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Remediation</p>
              <div className="space-y-2">
                <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Effort Required</span>
                  <span className="text-sm font-bold text-gray-900">{codeObject.remediation_effort_hours} hours</span>
                </div>
                <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Status</span>
                  <span className="text-sm font-bold text-gray-900 capitalize">{codeObject.remediation_status.replace('_', ' ')}</span>
                </div>
              </div>
            </div>

            {codeObject.break_type && (
              <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl">
                <p className="text-sm font-bold text-red-900 mb-1 flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Breaking Change
                </p>
                <p className="text-sm text-red-700 capitalize">{codeObject.break_type.replace('_', ' ')}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'analysis' && (
          <div className="space-y-4">
            {codeObject.ai_suggestion ? (
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Brain className="w-5 h-5 text-purple-600" />
                  <h4 className="text-lg font-bold text-gray-900">AI Analysis</h4>
                </div>
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6">
                  <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">{codeObject.ai_suggestion}</pre>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Sparkles className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-600 mb-6">No AI analysis available yet</p>
                <button
                  onClick={() => onAnalyze(codeObject)}
                  disabled={analyzing}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all font-medium disabled:opacity-50 inline-flex items-center space-x-2"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Analyzing...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      <span>Run AI Analysis</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'remediation' && (
          <div className="space-y-4">
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-3">Remediation Plan</h4>
              
              <div className="space-y-3">
                <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
                  <p className="text-sm font-semibold text-blue-900 mb-2">Estimated Effort</p>
                  <p className="text-3xl font-bold text-blue-700">{codeObject.remediation_effort_hours}</p>
                  <p className="text-sm text-blue-600">hours</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm font-semibold text-gray-900 mb-3">Remediation Steps</p>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                    <li>Review AI analysis and suggestions</li>
                    <li>Update code to S/4HANA compatible APIs</li>
                    <li>Refactor deprecated function calls</li>
                    <li>Update table structures if needed</li>
                    <li>Test in development environment</li>
                    <li>Create transport request</li>
                  </ol>
                </div>

                <button className="w-full px-4 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium flex items-center justify-center space-x-2">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>Mark as Completed</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Upload Code Modal
const UploadCodeModal = ({ onClose, projectId, onUploadComplete }) => {
  const [formData, setFormData] = useState({
    object_name: '',
    object_type: 'Program',
    description: '',
    lines_of_code: 0,
    complexity_score: 5.0,
    usage_frequency: 0,
    priority: 'medium'
  });
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      await axios.post(`${API}/custom-code`, {
        ...formData,
        project_id: projectId,
        remediation_status: 'pending',
        last_modified: new Date().toISOString()
      });
      
      await onUploadComplete();
      onClose();
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload code object');
    } finally {
      setUploading(false);
    }
  };

  const objectTypes = ['Program', 'Function Module', 'Class', 'Method', 'Include', 'Report', 'Form', 'Enhancement'];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <h3 className="text-3xl font-bold text-gray-900 mb-6">Upload Custom Code Object</h3>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Object Name *</label>
              <input
                type="text"
                required
                value={formData.object_name}
                onChange={(e) => setFormData({ ...formData, object_name: e.target.value })}
                placeholder="Z_CUSTOM_PROGRAM"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Object Type *</label>
              <select
                value={formData.object_type}
                onChange={(e) => setFormData({ ...formData, object_type: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {objectTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              placeholder="Describe the purpose of this code object"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Lines of Code</label>
              <input
                type="number"
                value={formData.lines_of_code}
                onChange={(e) => setFormData({ ...formData, lines_of_code: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Complexity (1-10)</label>
              <input
                type="number"
                step="0.1"
                min="1"
                max="10"
                value={formData.complexity_score}
                onChange={(e) => setFormData({ ...formData, complexity_score: parseFloat(e.target.value) })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Usage Frequency</label>
              <input
                type="number"
                value={formData.usage_frequency}
                onChange={(e) => setFormData({ ...formData, usage_frequency: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
            <div className="grid grid-cols-4 gap-3">
              {['low', 'medium', 'high', 'critical'].map((priority) => (
                <label
                  key={priority}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all text-center ${
                    formData.priority === priority
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="priority"
                    value={priority}
                    checked={formData.priority === priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="hidden"
                  />
                  <p className="font-bold text-gray-900 capitalize">{priority}</p>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all font-medium disabled:opacity-50 flex items-center space-x-2"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  <span>Upload Object</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
